import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Copy, Power, PowerOff, Radio, ServerCog } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function Ingestion() {
  const [info, setInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/ingest/info"), api.get("/settings")]);
      setInfo(a.data); setSettings(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        const { data } = await api.get("/ingest/log", { params: { limit: 50 } });
        setLogs(data);
      } catch {}
    };
    loadLogs();
    const iv = setInterval(loadLogs, 3000);
    return () => clearInterval(iv);
  }, []);

  const toggleSim = async () => {
    setLoading(true);
    try {
      const next = !settings.simulator_enabled;
      const { data } = await api.put("/settings", { simulator_enabled: next });
      setSettings(data);
      toast.success(`Simulator ${next ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied to clipboard"); };

  if (!info || !settings) return <div className="text-[#9BA4B5] font-mono-metric">LOADING…</div>;

  const url = `${BACKEND}${info.endpoint}`;
  const curl = `curl -X POST '${url}' \\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify(info.example_payload)}'`;

  return (
    <div className="space-y-6">
      <div>
        <div className="scada-label">REAL-TIME DATA</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">Gateway Ingestion</h1>
        <p className="text-[#9BA4B5] text-sm mt-2 max-w-2xl">Point your PLC/Modbus/MQTT/OPC-UA gateway at the endpoint below. No authentication required — just POST JSON. Sensor → PLC → Gateway → Server → SCADA.</p>
      </div>

      {/* Simulator toggle */}
      <div className="scada-panel p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {settings.simulator_enabled
            ? <Power className="text-[#FFB300]" size={22} />
            : <PowerOff className="text-[#00E676]" size={22} />}
          <div>
            <div className="font-heading font-bold text-lg">Demo Simulator</div>
            <div className="text-[#9BA4B5] text-sm">{settings.simulator_enabled ? "Generating random values every 2s (disable when live gateway is connected)" : "Disabled — system uses ONLY live gateway data"}</div>
          </div>
        </div>
        <button onClick={toggleSim} disabled={loading} data-testid="toggle-simulator"
          className={`border px-5 py-2 font-mono-metric uppercase tracking-wider text-xs ${settings.simulator_enabled ? "border-[#FFB300] text-[#FFB300] hover:bg-[#FFB300]/10" : "border-[#00E676] text-[#00E676] hover:bg-[#00E676]/10"}`}>
          {settings.simulator_enabled ? "Disable Simulator" : "Enable Simulator"}
        </button>
      </div>

      {/* Endpoint */}
      <div className="scada-panel p-5 space-y-4">
        <div className="flex items-center gap-2 scada-label"><ServerCog size={12} /> GATEWAY ENDPOINT — NO AUTH REQUIRED</div>

        <div>
          <div className="scada-label mb-1">URL (POST)</div>
          <div className="flex">
            <input readOnly value={url} className="flex-1 border bg-[#0B0D0F] px-3 py-2 text-sm font-mono-metric text-[#00E5FF]" data-testid="ingest-url" />
            <button onClick={() => copy(url)} className="border border-l-0 border-[#2C3238] px-3 hover:text-[#00E5FF]"><Copy size={14} /></button>
          </div>
        </div>

        <div>
          <div className="scada-label mb-1">CURL EXAMPLE</div>
          <pre className="border border-[#2C3238] bg-[#0B0D0F] p-3 text-[11px] font-mono-metric text-[#E3E8EE] overflow-x-auto">{curl}</pre>
          <button onClick={() => copy(curl)} className="mt-1 border border-[#2C3238] text-[#9BA4B5] hover:text-[#E3E8EE] hover:border-[#E3E8EE] px-3 py-1 font-mono-metric uppercase tracking-wider text-[10px] flex items-center gap-1"><Copy size={10} /> Copy</button>
        </div>

        <div>
          <div className="scada-label mb-1">SINGLE PAYLOAD FORMAT</div>
          <pre className="border border-[#2C3238] bg-[#0B0D0F] p-3 text-[11px] font-mono-metric text-[#9BA4B5] overflow-x-auto">{`{
  "DeviceID": "1",
  "DT": "1782934001",      // optional Unix timestamp (or "timestamp" ISO string)
  "P1": "1305",
  "P2": "2004",
  "P3": "1840"
}`}</pre>
        </div>

        <div>
          <div className="scada-label mb-1">BATCH PAYLOAD (optional)</div>
          <pre className="border border-[#2C3238] bg-[#0B0D0F] p-3 text-[11px] font-mono-metric text-[#9BA4B5] overflow-x-auto">{`{
  "readings": [
    {"DeviceID":"1","DT":"1782934001","P1":"1147","P2":"1821"},
    {"DeviceID":"2","P1":"1150","P2":"1822"}
  ]
}`}</pre>
        </div>
      </div>

      {/* Devices */}
      <div className="scada-panel">
        <div className="px-4 py-3 border-b border-[#2C3238] flex items-center gap-2 scada-label"><Radio size={12} /> REGISTERED DEVICES (configure External DeviceID per device)</div>
        <div className="grid grid-cols-12 px-4 py-2 scada-label border-b border-[#2C3238]">
          <div className="col-span-5">NAME</div>
          <div className="col-span-3">DEVICE_ID (DB)</div>
          <div className="col-span-4">EXTERNAL ID (GATEWAY)</div>
        </div>
        {info.devices.map(d => (
          <div key={d.id} className="grid grid-cols-12 px-4 py-2 text-xs font-mono-metric border-b border-[#2C3238]/60">
            <div className="col-span-5 text-[#E3E8EE]">{d.name}</div>
            <div className="col-span-3 text-[#5A6573]">{d.id.slice(0, 8)}…</div>
            <div className="col-span-4 text-[#00E5FF]">{d.external_id || "(not set)"}</div>
          </div>
        ))}
      </div>

      {/* Live activity */}
      <div className="scada-panel" data-testid="ingest-activity">
        <div className="px-4 py-3 border-b border-[#2C3238] flex items-center gap-2 scada-label">
          <Radio size={12} className="text-[#00E676] animate-pulse" /> LIVE INGEST ACTIVITY (last 50, auto-refresh 3s)
        </div>
        <div className="grid grid-cols-12 px-4 py-2 scada-label border-b border-[#2C3238]">
          <div className="col-span-2">TIME</div>
          <div className="col-span-1">STATUS</div>
          <div className="col-span-1">IP</div>
          <div className="col-span-2">CONTENT-TYPE</div>
          <div className="col-span-1">ACCEPTED</div>
          <div className="col-span-1">ERRORS</div>
          <div className="col-span-4">BODY</div>
        </div>
        {logs.length === 0 && (
          <div className="px-4 py-10 text-center text-[#5A6573] font-mono-metric text-sm">
            Waiting for gateway to POST… endpoint URL is above.
          </div>
        )}
        {logs.map((l) => (
          <details key={l.id} className="border-b border-[#2C3238]/60">
            <summary className="grid grid-cols-12 px-4 py-2 text-xs font-mono-metric cursor-pointer hover:bg-[#1E2226]/40">
              <div className="col-span-2 text-[#9BA4B5]">{new Date(l.ts).toLocaleTimeString()}</div>
              <div className="col-span-1 uppercase" style={{ color: l.status === 200 ? "#00E676" : l.status === 401 ? "#FFB300" : "#FF3B30" }}>{l.status}</div>
              <div className="col-span-1 text-[#9BA4B5] truncate">{l.ip || "—"}</div>
              <div className="col-span-2 text-[#9BA4B5] truncate">{l.content_type || "—"}</div>
              <div className="col-span-1 text-[#00E676]">{l.accepted || 0}</div>
              <div className="col-span-1" style={{ color: l.errors ? "#FFB300" : "#5A6573" }}>{l.errors || 0}</div>
              <div className="col-span-4 text-[#5A6573] truncate">{l.note} · {l.raw_body?.slice(0, 60)}</div>
            </summary>
            <pre className="text-[10px] px-4 py-2 bg-[#0B0D0F] text-[#9BA4B5] font-mono-metric overflow-x-auto whitespace-pre-wrap break-all">{l.raw_body || "(empty)"}</pre>
          </details>
        ))}
      </div>
    </div>
  );
}
